import { FunctionComponent, useCallback, useState } from 'react';
import { Button, Stack, SvgIcon, Typography, useTheme } from '@mui/material';
import ArrowDownOnSquareIcon from '@heroicons/react/24/solid/ArrowDownOnSquareIcon';
import { CustomersSearch } from '@/pageContents/customers/CustomersSearch';
import { StarjackTable } from '@/components/Table/v2/Table';
import {
    GetCustomerSearchParams,
    useGetCustomersQuery,
} from '@/api/v2/customer';
import { Customer } from '@/models/customer';
import DocumentMagnifyingGlassIcon from '@heroicons/react/24/solid/DocumentMagnifyingGlassIcon';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';
import { DeleteCustomerConfirmation } from './DeleteCustomerConfirmation';
import IconButton from '@mui/material/IconButton';
import useDebouncedEffect from '@/models/useDebouncedEffect';
import { downloadContentGeneric } from '@/utils';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';
import { urlBuilder } from '@/api/utils';
import Link from '@mui/material/Link';

const downloadData = async (
    userSearchTerm: string,
    jwt: string
): Promise<string | null> => {
    const response = await fetch(
        urlBuilder(
            '/auth',
            `customers/customers.xlsx${
                userSearchTerm ? `?search=${userSearchTerm}` : ''
            }`
        ),
        {
            method: 'GET',
            headers: {
                Authorization: `bearer ${jwt}`,
            },
        }
    );

    if (response.ok) {
        return URL.createObjectURL(
            new Blob([await response.blob()], {
                type: 'data:application/vnd.ms-excel;base64',
            })
        );
    } else {
        // as long as there is no detailed error message - null is enough
        return null;
    }
};

export const CustomersContent: FunctionComponent = () => {
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [searchParams, setSearchParams] = useState<GetCustomerSearchParams>({
        page: 0,
        size: 10,
        sort: 'firstname,lastname',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(
        null
    );
    const { data, isLoading } = useGetCustomersQuery(searchParams);
    const { addNotification } = useNotification();
    const { jwt } = useAuthentication();
    const theme = useTheme();

    const onDownloadClick = useCallback(async () => {
        const fileData = await downloadData(userSearchTerm, jwt);

        if (fileData !== null) {
            downloadContentGeneric('Kunden.xlsx', fileData);
        } else {
            addNotification({
                type: 'error',
                message:
                    'Fehler beim Download. Bitte später noch einmal versuchen',
            });
        }
    }, [userSearchTerm]);

    const onDeleteClick = useCallback((customer: Customer) => {
        setCustomerToDelete(customer);
        setDeleteDialogOpen(true);
    }, []);

    const onDeleteSuccess = useCallback(() => {
        addNotification({
            type: 'success',
            message: 'Kunde erfolgreich gelöscht',
        });
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
    }, [addNotification]);

    const onDeleteError = useCallback(
        (message: string) => {
            addNotification({
                type: 'error',
                message,
            });
        },
        [addNotification]
    );

    const onDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
    }, []);

    useDebouncedEffect(
        () => {
            if (userSearchTerm) {
                setSearchParams((prevState) => ({
                    ...prevState,
                    page: 0,
                    search: userSearchTerm,
                }));
            } else if (!userSearchTerm && searchParams.search) {
                setSearchParams(({ search, ...rest }) => {
                    void search;

                    return {
                        ...rest,
                        page: 0,
                    };
                });
            }
        },
        250,
        [userSearchTerm]
    );

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Kunden
            </Typography>

            <Stack alignItems='flex-start' spacing={theme.page.verticalSpacing}>
                <Button
                    onClick={onDownloadClick}
                    color='inherit'
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <ArrowDownOnSquareIcon />
                        </SvgIcon>
                    }
                >
                    Exportieren
                </Button>

                <CustomersSearch onChange={setUserSearchTerm} />

                <StarjackTable<Customer>
                    count={data?.totalElements || 0}
                    items={data?.content ?? []}
                    columns={[
                        {
                            label: 'E-Mail',
                            itemTransform: (customer) => customer.email,
                        },
                        {
                            label: 'Vorname',
                            itemTransform: (customer) => customer.firstname,
                        },
                        {
                            label: 'Nachname',
                            itemTransform: (customer) => customer.lastname,
                        },
                        {
                            label: 'Marketing-Akzeptanzdatum',
                            itemTransform: (customer) =>
                                customer?.marketingAcceptance
                                    ? customer.marketingAcceptance
                                    : '-',
                        },
                        {
                            label: 'Gast',
                            itemTransform: (customer) =>
                                customer.guest ? 'Ja' : 'Nein',
                        },
                        {
                            label: 'Details',
                            itemTransform: (customer) => (
                                <Link href={`/kunden/${customer.id}`}>
                                    <IconButton
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <SvgIcon>
                                            <DocumentMagnifyingGlassIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Link>
                            ),
                            options: {
                                rowCellProps: {
                                    align: 'center',
                                },
                            },
                        },
                        {
                            label: 'Löschen',
                            itemTransform: (customer) => (
                                <IconButton
                                    onClick={() => onDeleteClick(customer)}
                                >
                                    <SvgIcon>
                                        <TrashIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                            options: {
                                rowCellProps: {
                                    align: 'center',
                                },
                            },
                        },
                    ]}
                    itemId='id'
                    rowsPerPage={searchParams.size}
                    onRowsPerPageChange={(nextValue) => {
                        setSearchParams((prevState) => ({
                            ...prevState,
                            size: nextValue,
                        }));
                    }}
                    onPageChange={(nextPage) => {
                        setSearchParams((prevState) => ({
                            ...prevState,
                            page: nextPage,
                        }));
                    }}
                    loading={isLoading}
                    currentPage={searchParams.page}
                />
            </Stack>

            <DeleteCustomerConfirmation
                open={deleteDialogOpen}
                customer={customerToDelete}
                onSuccess={onDeleteSuccess}
                onError={onDeleteError}
                onCancel={onDeleteCancel}
            />
        </>
    );
};
